import { PLAN_PROPERTIES_MAP, getPlanFeatures } from '#constants';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  List,
  ListIcon,
  ListItem,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { IoCheckmarkCircleSharp, IoCloseCircleSharp } from 'react-icons/io5';
import { useEffect, useState } from 'react';

import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';

function SubscriptionSummary() {
  const { sub } = useUIStore((state) => ({ sub: state.sub }), shallow);
  const { plan } = sub;
  return (
    <Box>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        letterSpacing="wider"
        color="gray.500"
        textTransform="uppercase"
        mb={2}
      >
        Currently Active Plan
      </Text>
      <PlanBox planSlug={plan.slug} />
    </Box>
  );
}

function PlanBox({ planSlug, outingPriority }) {
  const { plans, promotion, getPlanCost, getCheckoutCost } = useUIStore(
    (state) => ({
      plans: state.plans,
      promotion: state.promotion,
      getPlanCost: state.getPlanCost,
      getCheckoutCost: state.getCheckoutCost,
    }),
    shallow
  );
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const [isExpanded, setIsExpanded] = useState(false);
  const plan = plans.find((plan) => plan.slug === planSlug);
  if (!plan) {
    return null;
  }
  // Get cost using store function
  const cost = getPlanCost(planSlug);
  const checkoutCost = getCheckoutCost();

  let freqText = '';
  if (plan.cadence === 'MONTHLY') {
    if (promotion?.code?.id) {
      freqText = 'for 1st month';
    } else {
      freqText = '/month';
    }
  } else if (plan.cadence === 'ANNUAL') {
    freqText = '/year';
  }

  const { bgColor } = PLAN_PROPERTIES_MAP[planSlug];
  const features = getPlanFeatures({ slug: planSlug, outingPriority });

  // Desktop / expanded: full list. Mobile collapsed: 2 until Read More.
  const visibleFeatures =
    isDesktop || isExpanded ? features : features.slice(0, 2);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Flex
      direction="column"
      p={2}
      py={[3, 4]}
      mx={[0, 5]}
      my={3}
      bgColor={bgColor}
      color="white"
      borderRadius={5}
    >
      <Heading size={['md', 'lg']} align="center" p={1}>
        {plan.name}
      </Heading>
      <Flex alignItems="baseline" justify="center">
        <Text fontSize={['xl', '2em']} alignItems={'baseline'}>
          $
        </Text>
        <Flex align={'baseline'}>
          {cost !== checkoutCost ? (
            <Flex direction="row" spacing={0} align={'baseline'}>
              <Text as="s" mr={2} fontSize={['4xl', '5xl']}>
                {cost}
              </Text>
              <Text fontSize={['5xl', '6xl']}>
                {checkoutCost}
              </Text>
            </Flex>
          ) : (
            <Text fontSize={['5xl', '6xl']}>{cost}</Text>
          )}
        </Flex>
        {freqText && (
          <Text fontSize={['1em', '1.2em']} ml={1}>
            {freqText}
          </Text>
        )}
      </Flex>
      <List spacing={3} p={3}>
        {visibleFeatures.map((feature) => {
          return (
            <ListItem key={feature.text} display="flex">
              <ListIcon
                boxSize={[5, 6]}
                as={
                  feature.present ? IoCheckmarkCircleSharp : IoCloseCircleSharp
                }
                color={feature.present ? 'white' : 'black'}
              />
              <Text fontSize={['sm', 'md']}>{feature.text}</Text>
            </ListItem>
          );
        })}
      </List>

      {/* Show Read More/Show Less button only on mobile */}
      {!isDesktop && features.length > 2 && (
        <Button
          variant="link"
          color="white"
          onClick={toggleExpand}
          alignSelf="center"
          mb={2}
          size="sm"
        >
          {isExpanded ? 'Show Less' : 'Read More'}
        </Button>
      )}
    </Flex>
  );
}
function Upgrade() {
  const { planSlug, setShowPricingModal, code } = useUIStore(
    (state) => ({
      planSlug: state.planSlug,
      setShowPricingModal: state.setShowPricingModal,
      code: state.code,
    }),
    shallow
  );
  if (code === 'YEAROFF') {
    return null;
  }
  const handleUpgrade = () => {
    setShowPricingModal(true);
  };
  if (!planSlug) return null;
  const { upgradeText, explainText } = PLAN_PROPERTIES_MAP[planSlug];
  return (
    <Flex direction="column" align="center" mt={[2, 6]}>
      <Button
        onClick={handleUpgrade}
        colorScheme="brand"
        display="flex"
        flexDirection="column"
        p={[6, 10]}
        py={[6, 8]}
      >
        <Text>{upgradeText}</Text>
        {explainText && (
          <Text fontStyle="italic" fontSize="sm" mt={[1, 2]}>
            {explainText}
          </Text>
        )}
      </Button>
    </Flex>
  );
}

export default function PlanSummary() {
  const { planSlug, sub, getPlans, getPromotion, promotion, outing } =
    useUIStore((state) => state, shallow);
  useEffect(() => {
    getPlans();
  }, []);

  const [discountCode, setDiscountCode] = useState('');
  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(false);
  const [loading, setLoading] = useState(false);

  if (sub?._id) {
    return (
      <Flex direction="column" justify="center" w="100%">
        <SubscriptionSummary />
      </Flex>
    );
  }

  const validateDiscountCode = async () => {
    setLoading(true);
    await getPromotion({ promotionCode: discountCode });
    setLoading(false);
  };

  const isInvalid = promotion && !promotion.isValid;
  return (
    <Box w="100%">
      <Text
        fontSize="sm"
        fontWeight="semibold"
        letterSpacing="wider"
        color="gray.500"
        textTransform="uppercase"
        mb={2}
      >
        Plan Summary
      </Text>
      <Flex
        direction="column"
        justify="center"
        fontFamily="Roboto, Arial, sans-serif"
        gap={[2, 4]}
      >
        <PlanBox planSlug={planSlug} outingPriority={outing?.priority} />
        <Flex direction="column" align={'center'}>
          <Upgrade />
          {/* <Flex direction="column" mx={4} mt={2} width={'300px'}>
            {!showDiscountCodeInput ? (
              <Button
                variant={'link'}
                onClick={() => setShowDiscountCodeInput(!showDiscountCodeInput)}
                colorScheme="brand"
              >
                Have a promotion code?
              </Button>
            ) : (
              promotion && promotion.isValid ? (
                <Text align={'center'} color={'brand'}>Promotion applied!</Text>
              ) : (
                <Flex direction={'column'}>
                  <Flex direction="row">
                    <Input
                      type="text"
                      value={discountCode}
                      onChange={(e) =>
                        setDiscountCode(e.target.value.toUpperCase())
                      }
                      placeholder="Enter discount code"
                      isInvalid={promotion?.hasFetched && isInvalid}
                      isDisabled={loading}
                      size={'sm'}
                    />
                    <Button
                      mx={2}
                      onClick={validateDiscountCode}
                      size={'sm'}
                      colorScheme="brand"
                      variant={'outline'}
                    >
                      Apply
                    </Button>
                  </Flex>
                  {promotion?.hasFetched && isInvalid && (
                    <Text color="red.500" fontSize="sm">
                      Invalid code
                    </Text>
                  )}
                </Flex>
              )
            )}
          </Flex> */}
        </Flex>
      </Flex>
    </Box>
  );
}
